import { StudioImage } from '../studio/UI';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { pieceName, collectionLabel, hasNamedCollection } from '../shopItems';
import { useCatalog } from '../catalogContext';
import { useCart } from '../cartContext';
import { FREE_SHIPPING_MINIMUM } from '../shipping';
import StorefrontLayout from './StorefrontLayout';
import TrimmedArtwork from './TrimmedArtwork';
import ProductCard from './ProductCard';

const ProductPage = () => {
    const { id } = useParams();
    const { shopItems, getShopItem } = useCatalog();
    const item = getShopItem(id);
    const { addToCart, getQuantity } = useCart();
    const [quantity, setQuantity] = useState(1);
    const [photo, setPhoto] = useState(0);

    useEffect(() => {
        window.scrollTo(0, 0);
        setQuantity(1);
        setPhoto(0);
    }, [id]);

    useEffect(() => {
        const photos = item?.images?.length ? item.images : [item?.image].filter(Boolean);
        if (photos.length < 2) {
            return undefined;
        }
        const onKey = (event) => {
            if (event.key === 'ArrowLeft') {
                setPhoto((n) => (n - 1 + photos.length) % photos.length);
            }
            if (event.key === 'ArrowRight') {
                setPhoto((n) => (n + 1) % photos.length);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [id, item]);

    if (!item) {
        return (
            <StorefrontLayout>
                <main className="productMissing">
                    <h1>Item not found</h1>
                    <Link to="/shop">Return to shop</Link>
                </main>
            </StorefrontLayout>
        );
    }

    const photos = item.images?.length ? item.images : [item.image].filter(Boolean);
    const activePhoto = photos[Math.min(photo, photos.length - 1)] || item.image;
    const { name, year } = pieceName(item, { shop: true });
    const inCart = getQuantity(item.id);
    const maxQuantity = Math.max(1, Number(item.available || 0));
    const canBuy = Number(item.available || 0) > 0;
    const namedCollection = collectionLabel(item);
    const others = shopItems.filter((other) => other.id !== item.id && !other.soldOut);
    const sameCollection = hasNamedCollection(item)
        ? others.filter((other) => other.collection === item.collection)
        : [];
    const rest = hasNamedCollection(item)
        ? others.filter((other) => other.collection !== item.collection)
        : others;
    const related = [...sameCollection, ...rest].slice(0, 4);

    return (
        <StorefrontLayout>
            <main className="productPage">
                <nav className="productCrumbs">
                    <Link to="/">Home</Link>
                    <span>/</span>
                    <Link to="/shop">Art Prints</Link>
                    {namedCollection ? <>
                        <span>/</span>
                        <span>{namedCollection}</span>
                    </> : null}
                    <span>/</span>
                    <span>{name}</span>
                </nav>

                <section className="productLayout">
                    <div className="productGallery">
                        <div className="productStage">
                            {photos.length > 1 ?
                                <button
                                    type="button"
                                    className="productHit productHitPrev"
                                    onClick={() => setPhoto((n) => (n - 1 + photos.length) % photos.length)}
                                    aria-label="Previous photo"
                                >
                                    <svg viewBox="0 0 24 24" aria-hidden="true">
                                        <path d="M14.5 4.5 7 12l7.5 7.5" />
                                    </svg>
                                </button>
                                : null}
                            <TrimmedArtwork src={activePhoto} alt={name} />
                            {photos.length > 1 ?
                                <button
                                    type="button"
                                    className="productHit productHitNext"
                                    onClick={() => setPhoto((n) => (n + 1) % photos.length)}
                                    aria-label="Next photo"
                                >
                                    <svg viewBox="0 0 24 24" aria-hidden="true">
                                        <path d="M9.5 4.5 17 12l-7.5 7.5" />
                                    </svg>
                                </button>
                                : null}
                        </div>
                        {photos.length > 1 ?
                            <div className="productThumbs" role="list">
                                {photos.map((src, index) => (
                                    <button
                                        key={`${src}-${index}`}
                                        type="button"
                                        className={index === photo ? 'productThumb productThumbActive' : 'productThumb'}
                                        onClick={() => setPhoto(index)}
                                        aria-label={`Photo ${index + 1}`}
                                    >
                                        <StudioImage src={src} alt="" sizes="80px" />
                                    </button>
                                ))}
                            </div>
                            : null}
                    </div>
                    <div className="productInfo">
                        <header className="pieceHeader">
                            <p className="pieceKicker">
                                <span>{item.category}</span>
                                {year ? <><span aria-hidden="true">/</span><span>{year}</span></> : null}
                                {item.edition ? <><span aria-hidden="true">/</span><span className="pieceEdition">{item.edition}</span></> : null}
                            </p>
                            <h1 className="pieceTitle">{name}</h1>
                        </header>
                        <p className="piecePrice">${item.price.toFixed(2)} USD</p>
                        {item.summary ? <p className="pieceSummary">{item.summary}</p> : null}
                        {item.detail ? <p className="pieceDetail">{item.detail}</p> : null}

                        {!canBuy ?
                            <button className="productCartButton" type="button" disabled>
                                {inCart ? 'In cart' : 'Sold out'}
                            </button>
                            :
                            <div className="productPurchase">
                                <span className="productQtyLabel">Quantity</span>
                                <div className="cartQtyRow productQty">
                                    <button type="button" aria-label="Decrease quantity" onClick={() => setQuantity((n) => Math.max(1, n - 1))}>−</button>
                                    <span>{quantity}</span>
                                    <button type="button" aria-label="Increase quantity" onClick={() => setQuantity((n) => Math.min(maxQuantity, n + 1))}>+</button>
                                </div>
                                <p className="productInCart">({inCart} in cart)</p>
                                <button className="productCartButton" type="button" onClick={() => addToCart(item, quantity)}>
                                    Add to cart
                                </button>
                            </div>
                        }

                        <p className="productNote">
                            {`Handmade print. Free shipping on U.S. orders over $${FREE_SHIPPING_MINIMUM}.`}
                        </p>
                    </div>
                </section>

                {related.length > 0 ?
                    <section className="productRelated">
                        <h2>You may also like</h2>
                        <div className="productGrid">
                            {related.map((other) => (
                                <ProductCard key={other.id} item={other} plain />
                            ))}
                        </div>
                    </section>
                    : null}
            </main>
        </StorefrontLayout>
    );
};

export default ProductPage;
